import { ElementRef, Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { TranslatorService } from './translator.service';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map } from 'rxjs';
import authHeader from '../helpers/auth-header';
import { PageLoggingModel } from '../interfaces/page-logging-model';
import NationState from '../interfaces/NationState';
import axios from 'axios';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';

@Injectable({
     providedIn: 'root'
})
export class GeneralService {
     public setRoute = '';
     private _Session_Key: string = "";
     public _Session_custID: string = "";
     constructor (public translatorService: TranslatorService, private http: HttpClient, private router: Router) { }

     openDocument(docName: string) {
          const docUrl = `${environment.APP_PSP_DOC_BASE_URL}/getByName/${docName}.pdf`;
          window.open(docUrl, '_blank')?.focus();
     }

     getTranslation(tag: string, translationFile: string = 'base') {
          if (tag) {
               const returnValue = this.translatorService.getTextForByTag(tag, translationFile)
               return returnValue;
          }
          return '';
     }

     get route() {
          return this.setRoute;
     }

     getLoginGovUrl(authCode?: string): any {
          const url = `${environment.APP_API_URL}/api/OIDCAuthentication/SignIn?authCode=${authCode}`;
          return this.http.get(url, { responseType: 'text' })
               .pipe(
                    map((response: any) => response),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     getFile(file: string): any {
          const url = `${environment.BASE_URL}/assets/content/agreements/${file}.htm`;
          return this.http.get(url, { responseType: 'text' })
               .pipe(
                    map((response: any) => response),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     getEndUserIp(): string | any {
          const ipUrl = 'https://api.ipify.org?format=json';
          return this.http.get(ipUrl);
     }

     setIsLoggingIn(code: string, state: string): void {
          localStorage.setItem('code', code);
          localStorage.setItem('state', state);
     }

     getIsLoggingIn() {
          const code: string = localStorage.getItem('code') as string;
          const state: string = localStorage.getItem('state') as string;
          if (code !== null && state !== null) {
               // code and state found in local storage. Return the values and remove from local storage
               localStorage.removeItem('code');
               localStorage.removeItem('state');
               return { "code": code, "state": state };
          } else {
               return null;
          }
     }

     maskEmail(email: string): string {
          const maskedEmail = email.split("");
          let index = 1;
          while (maskedEmail[index] !== "@" && index < maskedEmail.length) {
               maskedEmail[index] = "*";
               index++;
          }
          return maskedEmail.join('');
     }

     pageLogging(page: string, customerID: any = "", sessionKey: string = "", error: string = "", info: string = "") {
          this._Session_Key = (sessionKey !== "" ? sessionKey : this._Session_Key);
          this._Session_custID = (customerID !== "" ? customerID : this._Session_custID);
          page = (page !== "" ? page : "NA");
          const customerID_ = customerID;
          const pageLogging_: PageLoggingModel = {
               session_Key: this._Session_Key,
               date_Time: "",
               page: page,
               customer_ID: customerID_ ?? "",
               error: error,
               info: info
          }

          const ah = authHeader();
          if (ah) {
               const url: string = `${environment.APP_API_URL}/api/v1/PageLogging/Auth/Log`;
               const config = {
                    headers: {
                         'Content-Type': 'application/json;charset=UTF-8',
                         'Authorization': authHeader()
                    }
               };
               return this.http.post(url, pageLogging_, config)
                    .pipe(
                         map((response: any) => {
                              if (response) {
                                   const r = response as unknown as PageLoggingModel;
                                   if (r && r.session_Key) {
                                        this._Session_Key = r.session_Key;
                                   }
                                   return r;
                              }
                              return '';
                         }),
                         catchError(error => {
                              console.error(error);
                              return '';
                         })
                    );

          } else {
               const url: string = `${environment.APP_API_URL}/api/v1/PageLogging/Log`;
               return this.http.post(url, pageLogging_)
                    .pipe(
                         map((response: any) => {
                              if (response) {
                                   const r = response as unknown as PageLoggingModel;
                                   if (r && r.session_Key) {
                                        this._Session_Key = r.session_Key;
                                   }
                                   return r;
                              }
                              return '';
                         }),
                         catchError(error => {
                              console.error(error);
                              return '';
                         })
                    );
          }
     }

     gridFooterTxt(footerArea: ElementRef) {
          if (footerArea) {
               const pagingElements = footerArea.nativeElement.parentElement?.querySelectorAll('[id$="-to"], [id$="-of"], [id$="-page"]');

               if (pagingElements) {
                    pagingElements.forEach((element: any) => {
                         let translationKey = '';
                         if (element.id.endsWith('-to')) {
                              translationKey = 'to_grid';
                         } else if (element.id.endsWith('-of')) {
                              translationKey = 'of_grid';
                         } else if (element.id.endsWith('-page')) {
                              translationKey = element.id.includes('of') ? 'of_grid' : 'page_grid';
                         }
                         element.textContent = this.getTranslation(translationKey, 'base');
                    });
               }
          }
     }

     getStates(): NationState[] | any {
          const url: string = `${environment.APP_API_URL}/api/v1/Geography/getNationStates`;
          return axios.get(url)
               .then((response) => (response.data as []))
               .catch((error) => {
                    console.error(error);
                    console.error('Error loading states.');
                    return [];
               });
     }

     getStatesByCountry(country: string): any {
          const url: string = `${environment.APP_API_URL}/api/v1/Geography/getStates?country=${country}`;
          return axios.get(url)
               .then((response) => (response.data as []))
               .catch((error) => {
                    console.error(error);
                    console.error('Error loading states.');
                    return [];
               });
     }

     getAPIToken() {
          const header = {
               'Content-Type': 'application/json;charset=UTF-8',
               'Authorization': authHeader()
          };
          const url: string = `${environment.APP_API_URL}/api/v1/webservice`;
          return this.http.get(url, { headers: header, responseType: 'text' })
               .pipe(
                    map((response: any) => {
                         return response
                    }),
                    catchError(error => {
                         console.error(error);
                         return '';
                    })
               );
     }

     getMaskedToken() {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const url: string = `${environment.APP_API_URL}/api/v1/webservice/getMaskedToken`;
          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         return response
                    }),
                    catchError(error => {
                         console.error(error);
                         return '';
                    })
               );
     }

     formatDate(theDate: DateTime) {
          return theDate.toFormat('MM/dd/yyyy');
     }

     getQueryParameters(url: string) {
          const regex = /[?&]([^=#]+)=([^&#]*)/g;
          const params: { [key: string]: string } = {};
          let match;

          while ((match = regex.exec(url)) !== null) {
               params[match[1]] = decodeURIComponent(match[2]);
          }

          return params;
     }
}
